import React, { FC } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHero, CtaBanner } from '../components/SharedComponents';
import EnquiryForm from '../components/EnquiryForm';
import { INDUSTRIES } from '../data/industries';
import { PRODUCTS } from '../data/products';
import { IMG, WHATSAPP_LINK } from '../data/constants';
import {
  Factory, ShoppingCart, Package, HeartPulse, Utensils, Pill,
  ShoppingBag, BookOpen, Building2, Plane, Gem, Shirt, Cpu,
  Hotel, HardHat, Sprout, Tag, Settings, Scan, Award, CheckCircle,
  Phone, MessageCircle, ChevronRight, ArrowRight, Layers
} from 'lucide-react';

// Maps industry icon string → Lucide component
const INDUSTRY_ICON_MAP: Record<string, FC<{ size?: number; color?: string }>> = {
  'factory':       Factory,
  'shopping-cart': ShoppingCart,
  'package':       Package,
  'heart-pulse':   HeartPulse,
  'utensils':      Utensils,
  'pill':          Pill,
  'shopping-bag':  ShoppingBag,
  'book-open':     BookOpen,
  'building-2':    Building2,
  'plane':         Plane,
  'gem':           Gem,
  'shirt':         Shirt,
  'cpu':           Cpu,
  'hotel':         Hotel,
  'hard-hat':      HardHat,
  'sprout':        Sprout,
};

// Use case icon map — using Lucide icons
const UC_ICON_MAP: Record<string, FC<{ size?: number; color?: string }>> = {
  'Product labeling':                     Tag,
  'Work-in-progress tracking':            Settings,
  'Asset management':                     Layers,
  'Quality control':                      Award,
  'Price tagging':                        Tag,
  'Billing at checkout counters':         ShoppingCart,
  'Inventory management':                 Package,
  'Shelf labeling':                       Layers,
  'Shipment tracking':                    Package,
  'Warehouse management':                 Factory,
  'Package identification':               Package,
  'Delivery confirmation':                CheckCircle,
  'Patient identification (wristbands)':  HeartPulse,
  'Medicine labeling':                    Pill,
  'Lab sample tracking':                  Scan,
  'Equipment management':                 Settings,
  'Packaging labels':                     Package,
  'Expiry date tracking':                 CheckCircle,
  'Batch tracking':                       Layers,
  'Compliance labeling':                  Award,
  'Drug identification':                  Pill,
  'Anti-counterfeiting':                  Award,
  'Regulatory compliance':                Award,
  'Serialization tracking':               Layers,
  'Order processing':                     ShoppingBag,
  'Shipping labels':                      Package,
  'Returns management':                   ArrowRight,
  'Inventory synchronization':            Layers,
  'Book tracking':                        BookOpen,
  'Student ID cards':                     Award,
  'Issue/return systems':                 ArrowRight,
  'Office equipment tracking':            Building2,
  'IT asset management':                  Cpu,
  'File/document tracking':               Layers,
  'Boarding passes':                      Plane,
  'Baggage tracking':                     Package,
  'Ticketing system':                     Tag,
  'Gold & silver price tagging':          Gem,
  'Jewellery inventory management':       Package,
  'POS billing':                          ShoppingCart,
  'Display tray labels':                  Layers,
  'Brand woven labels':                   Shirt,
  'Care instruction labels':              CheckCircle,
  'Size labels':                          Tag,
  'Hang tags':                            Tag,
  'Serial number labeling':               Layers,
  'Warranty void labels':                 Award,
  'Component tracking':                   Cpu,
  'Product identification':               Scan,
  'Linen and asset tracking':             Hotel,
  'Room service labels':                  Tag,
  'Machinery asset tags':                 Settings,
  'Tool tracking':                        Settings,
  'Crop and produce tracking':            Sprout,
  'Pesticide labeling':                   Tag,
  'Outdoor asset tags':                   Sprout,
};

// Maps industry relatedProducts strings to product paths
const PRODUCT_PATH_MAP: Record<string, string> = {
  'Barcode Labels':           '/barcode-labels',
  'Thermal Transfer Ribbons': '/ttr-ribbons',
  'TTR Ribbons':              '/ttr-ribbons',
  'Barcode Printers':         '/printers',
  'Barcode Scanners':         '/scanners',
  'Jewellery Labels':         '/jewellery-labels',
  'Taffeta Labels':           '/taffeta-labels',
  'Retail POS':               '/retail-pos',
  'Retail POS / Billing':     '/retail-pos',
};

const IndustryDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const industry = INDUSTRIES.find(ind => ind.id === id);
  if (!industry) return <Navigate to="/industries" replace />;

  return (
    <>
      <PageHero
        breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Industries', link: '/industries' }, { label: industry.title }]}
        title={industry.title}
        subtitle={industry.description}
      />

      <section style={{ background: 'var(--light)' }}>
        <div className="container">
          <div className="ind-page-layout">
            {/* LEFT - Main Content */}
            <div>
              <img
                className="ind-hero-img"
                src={industry.image}
                alt={industry.title}
                onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
              />

              {/* Use Cases */}
              <div className="ind-use-cases">
                <h3>How Barcode Solutions Help in {industry.title}</h3>
                <div className="use-case-grid">
                  {industry.useCases.map(uc => {
                    const UCIcon = UC_ICON_MAP[uc] || CheckCircle;
                    return (
                      <div key={uc} className="use-case-item">
                        <span className="uc-icon"><UCIcon size={20} color="var(--accent)" /></span>
                        <p>{uc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Label Categories for this Industry */}
              {industry.labelCategories && industry.labelCategories.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 20, color: 'var(--primary)', marginBottom: 8 }}>
                    Label &amp; Barcode Categories for {industry.title}
                  </h3>
                  <p style={{ color: 'var(--gray)', fontSize: 13.5, marginBottom: 20, lineHeight: 1.6 }}>
                    Click on any category below to explore products and get a quote.
                  </p>
                  <div className="ind-label-cats-grid">
                    {industry.labelCategories.map(lc => (
                      <Link key={lc.name} to={lc.link} className="ind-label-cat-card">
                        <img
                          src={lc.image}
                          alt={lc.name}
                          className="ind-label-cat-img"
                          onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
                        />
                        <div className="ind-label-cat-body">
                          <h4>{lc.name}</h4>
                          <p>{lc.description}</p>
                          <span className="ind-label-cat-cta">
                            View Details <ChevronRight size={13} />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div style={{ marginBottom: 28 }}>
                <h3 style={{ fontFamily: 'Montserrat', fontWeight: 800, fontSize: 20, color: 'var(--primary)', marginBottom: 12 }}>Key Applications</h3>
                <div className="ind-tags">
                  {industry.tags.map(t => <span key={t} className="ind-tag">{t}</span>)}
                </div>
              </div>

              {/* Related Products */}
              <div className="ind-products-row">
                <h3>Recommended Products for {industry.title}</h3>
                <div className="ind-prod-chips">
                  {industry.relatedProducts.map(rp => (
                    <Link key={rp} to={PRODUCT_PATH_MAP[rp] || '/products'} className="ind-prod-chip">
                      {rp} <ArrowRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Product preview cards */}
              <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {PRODUCTS.filter(p => industry.relatedProducts.some(rp =>
                  p.title.includes(rp.replace(' / Billing', '').replace('Thermal Transfer Ribbons', 'Thermal Transfer')) ||
                  rp.includes(p.title.split(' ')[0])
                )).slice(0, 4).map(p => (
                  <Link key={p.id} to={p.path} className="prod-card" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="prod-img-wrap">
                      <img
                        className="prod-img"
                        src={p.image}
                        alt={p.title}
                        style={{ height: 140 }}
                        onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
                      />
                      <span className={`prod-badge b-${p.badgeColor}`}>{p.badge}</span>
                    </div>
                    <div className="prod-body">
                      <h3>{p.title}</h3><p>{p.description}</p>
                      <div className="prod-foot"><span className="pcat">{p.category}</span><span className="parrow"><ArrowRight size={15} /></span></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* RIGHT - Sticky Enquiry */}
            <div className="side-enquiry">
              <EnquiryForm productName={`${industry.title} Solutions`} />
              <div style={{ background: 'var(--primary)', borderRadius: 12, padding: 22, marginTop: 16, color: '#fff' }}>
                <p style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Phone size={14} /> Talk to Our Expert
                </p>
                <a href="tel:+919319299973" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 6, textDecoration: 'none' }}>+91 9319299973</a>
                <a href="tel:+919319299974" style={{ color: 'var(--accent2)', fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 14, textDecoration: 'none' }}>+91 9319299974</a>
                <p style={{ fontSize: 12, color: '#64748b' }}>Mon–Sat: 9:30 AM – 6:30 PM</p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#25d366', color: '#fff', fontFamily: 'Montserrat', fontWeight: 700, fontSize: 13, padding: '10px 16px', borderRadius: 8, textDecoration: 'none', marginTop: 14, justifyContent: 'center' }}>
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner image={IMG.ctaIndustry}
        title={`Need Barcode Solutions for ${industry.title}?`}
        subtitle="Get customized solutions tailored for your industry requirements."
        primaryBtnText="Get Free Consultation →" primaryBtnLink="/contact"
        secondaryBtnText="WhatsApp" secondaryBtnLink={WHATSAPP_LINK} secondaryIsExternal />
    </>
  );
};

// Industries listing page
export const IndustriesPage: FC = () => (
  <>
    <PageHero breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Industries We Serve' }]}
      title="Industries We Serve" subtitle="Barcode and label solutions tailored for every sector — from manufacturing to retail." />
    <section style={{ background: 'var(--light)' }}>
      <div className="container">
        <span className="section-tag">Who We Serve</span>
        <h2 className="section-title">Trusted Across All Industries</h2>
        <p className="section-sub" style={{ marginBottom: 40 }}>We understand each industry has unique labeling requirements. Click on any industry to explore specific solutions.</p>
        <div className="ind-full-grid">
          {INDUSTRIES.map(ind => {
            const IconComp = INDUSTRY_ICON_MAP[ind.icon] || Factory;
            return (
              <Link key={ind.id} to={`/industries/${ind.id}`} className="ind-full-card">
                <img
                  className="ind-full-card-img"
                  src={ind.image}
                  alt={ind.title}
                  onError={(e) => { (e.target as HTMLImageElement).style.background = '#e2e8f0'; }}
                />
                <div className="ind-full-body">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <IconComp size={18} color="var(--accent)" /> {ind.title}
                  </h3>
                  <p>{ind.description}</p>
                  <div className="ind-tags">{ind.tags.slice(0, 3).map(t => <span key={t} className="ind-tag">{t}</span>)}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
    <CtaBanner image={IMG.ctaManuf} title="Don't See Your Industry?" subtitle="We serve many more sectors. Contact us to discuss your requirements."
      primaryBtnText="Talk to Our Expert →" primaryBtnLink="/contact" />
  </>
);

export default IndustryDetailPage;
